import { Button } from "@legal-500/legal500-design-system";
import { recipientStatusLabel } from "./recipient-status";
import { selectionCount, type RecipientSelection } from "./selection";
import styles from "./MailingGroups.module.css";

type Props = {
  selection: RecipientSelection;
  // Closes the modal and returns the operator to the candidate table.
  onEdit: () => void;
};

type Criterion = { key: string; label?: string; value: string };

// Derives the read-only criteria chips from the selection descriptor. Firm/region
// display the names the descriptor carries (resolved from the projection facets),
// falling back to a generic "Filtered" marker if a name failed to resolve; status
// is an enum, so its label is exact.
function buildCriteria(selection: RecipientSelection): Criterion[] {
  if (selection.mode === "explicit") {
    return [{ key: "audience", label: "Audience", value: "Hand-picked" }];
  }

  const criteria: Criterion[] = [
    { key: "audience", label: "Audience", value: "All matching filter" },
  ];
  criteria.push({
    key: "region",
    label: "Region",
    value: selection.regionId === undefined ? "All" : selection.regionName || "Filtered",
  });
  if (selection.firmRef) {
    criteria.push({ key: "firm", label: "Firm", value: selection.firmName || "Filtered" });
  }
  criteria.push({
    key: "status",
    label: "Status",
    value: selection.status ? recipientStatusLabel(selection.status) : "All",
  });
  if (selection.excludeEmails.length > 0) {
    criteria.push({ key: "excluded", value: `${selection.excludeEmails.length} excluded` });
  }
  return criteria;
}

// Read-only summary of the frozen audience shown at the top of the New send
// modal: a big count plus the criteria that produced it. No recipient list —
// the "who" was decided on the table.
export function AudienceCard({ selection, onEdit }: Props) {
  const count = selectionCount(selection);
  const criteria = buildCriteria(selection);

  return (
    <div>
      <div className={styles.modalSectionLabel}>Audience</div>
      <div className={styles.audienceCard}>
        <div className={styles.audienceTop}>
          <div className={styles.audienceBig}>{count.toLocaleString()}</div>
          <div className={styles.audienceLabel}>recipients will be sent</div>
        </div>
        <div className={styles.criteria}>
          {criteria.map(c => (
            <span key={c.key} className={styles.crit}>
              {c.label ? `${c.label}: ` : null}
              <b>{c.value}</b>
            </span>
          ))}
        </div>
        <div className={styles.audienceEditRow}>
          <Button variant="text" color="primary" size="small" onClick={onEdit}>
            ← Edit selection on the list
          </Button>
        </div>
      </div>
    </div>
  );
}
